import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AiDocumentationFieldEnum,
  useAiDismissDocumentationDetectionMutation,
} from 'graphql-common';
import { showGraphQLErrors } from '@lib/utils/graphQLErrors';

// Settles a field's AI-detection state (Cancel on a Failed/MultipleCodes
// affordance) client-side-optimistically, then persists it via
// `aiDismissDocumentationDetection` (marks `aiDetectionStatus[field] =
// DISMISSED`, clears `suggestedCodes` for CODE) so it stays settled across
// reloads. The mutation result merges into the Apollo cache by id, so no
// manual refetch is needed. Shared between the View and Edit screens.
export default function useAiDetectionDismiss(docId: string | undefined) {
  const { t } = useTranslation();
  const [dismissMutation] = useAiDismissDocumentationDetectionMutation();
  const [dismissed, setDismissed] = useState<Set<AiDocumentationFieldEnum>>(
    new Set(),
  );

  const isDismissed = useCallback(
    (field: AiDocumentationFieldEnum) => dismissed.has(field),
    [dismissed],
  );

  const dismiss = useCallback(
    (field: AiDocumentationFieldEnum) => {
      if (!docId) return;
      setDismissed((prev) => new Set(prev).add(field));
      dismissMutation({
        variables: { id: docId, attributes: { field } },
        onError: (error) => {
          showGraphQLErrors(error, t);
          setDismissed((prev) => {
            const next = new Set(prev);
            next.delete(field);
            return next;
          });
        },
      });
    },
    [dismissMutation, docId, t],
  );

  // "Done reviewing" — settles every annotation on the document at once.
  // Omitting `field` (attributes: {}) tells the BE to dismiss all of them in
  // one write, rather than one `dismiss()` call per flagged field.
  const dismissAll = useCallback(() => {
    if (!docId) return;
    const previouslyDismissed = dismissed;
    setDismissed(new Set(Object.values(AiDocumentationFieldEnum)));
    dismissMutation({
      variables: { id: docId, attributes: {} },
      onError: (error) => {
        showGraphQLErrors(error, t);
        setDismissed(previouslyDismissed);
      },
    });
  }, [dismissMutation, dismissed, docId, t]);

  return { isDismissed, dismiss, dismissAll };
}
